<template>
  <Teleport to="body">
    <div
      v-if="innerOpen"
      class="ll-calendar__event-list-layer"
      @click.self="closePanel"
      @contextmenu.prevent
    >
      <div
        class="ll-calendar__event-list-panel"
        :style="props.panelStyle"
        @click.stop
      >
        <div class="ll-calendar__event-list-header">
          <div class="ll-calendar__event-list-heading">{{ title }}</div>
          <button
            type="button"
            class="ll-calendar__event-list-close"
            aria-label="关闭"
            @click="closePanel"
          >
            ×
          </button>
        </div>

        <div class="ll-calendar__event-list-container">
          <ll-empty v-if="events.length === 0" title="无日程" />
          <div v-else class="ll-calendar__event-list">
            <div
              v-for="(event, idx) in events"
              :key="`${getEventIdentifier(event)}-${idx}`"
              class="ll-calendar__event-list-item"
              @click="handleSelect(event, $event as MouseEvent)"
              @contextmenu.prevent.stop="
                handleContextmenu(event, $event as MouseEvent)
              "
            >
              <div class="ll-calendar__event-multiday-single">
                <span
                  v-if="event.tag"
                  class="ll-calendar__event-tag"
                  :style="
                    event.tagColor ? { backgroundColor: event.tagColor } : {}
                  "
                  >{{ event.tag }}</span
                >{{ getEventTitle(event) || '未命名日程' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <calendar-edit-panel
    :ref="bindEditPanelRef"
    :open="editPopoverOpen"
    :presentation-mode="editPresentationMode"
    :placement="editPopoverPlacement"
    :style="editPopoverStyle"
    :boundary-style="editBoundaryStyle"
    :title="editingEventTitle"
    :form-data="editFormData"
    :form-columns="editFormColumns"
    :is-creating-event="isCreatingEvent"
    :current-editable-event="currentEditableEvent"
    @mask-close="handleMaskClose"
    @switch-presentation="handleSwitchPresentation"
    @confirm="handleConfirmEdit"
    @confirm-and-add="handleConfirmAndAdd"
    @delete-edit-event="handleDeleteEditEvent"
  >
    <template v-if="$slots['edit-form']" #edit-form="{ data }">
      <slot name="edit-form" :data="data" />
    </template>
  </calendar-edit-panel>

  <calendar-context-menu
    :ref="bindContextMenuPanelRef"
    :open="contextMenuOpen"
    :style="contextMenuStyle"
    :event="contextMenuEvent"
    :current-editable-event="currentEditableEvent"
    @close="closeContextMenu"
    @edit="handleContextMenuEdit"
    @copy="handleContextMenuCopy"
    @remove="handleContextMenuDelete"
  >
    <template #event-contextmenu="{ event, close, edit, copy, remove }">
      <slot
        name="event-contextmenu"
        :event="event"
        :close="close"
        :edit="edit"
        :copy="copy"
        :remove="remove"
      />
    </template>
  </calendar-context-menu>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import CalendarContextMenu from './calendar-context-menu.vue'
import CalendarEditPanel from './calendar-edit-panel.vue'
import type { CalendarEvent } from './calendar'

const props = defineProps({
  open: { type: Boolean, required: true },
  title: { type: String, required: true },
  events: { type: Array, required: true },
  getEventIdentifier: { type: Function, required: true },
  getEventTitle: { type: Function, required: true },
  getEventTimeLabel: { type: Function, required: true },
  onSelectEvent: { type: Function, required: true },
  onContextmenuEvent: { type: Function, required: true },
  editPopoverOpen: { type: Boolean, required: true },
  editPresentationMode: { type: String, required: true },
  editPopoverPlacement: { type: String, required: true },
  contextMenuOpen: { type: Boolean, required: true },
  contextMenuEvent: { required: true },
  contextMenuStyle: { type: Object, required: true },
  editPopoverStyle: { type: Object, required: true },
  editBoundaryStyle: { type: Object, required: false },
  editFormData: { type: Object, required: true },
  editFormColumns: { type: Array, required: true },
  isCreatingEvent: { type: Boolean, required: true },
  editingEventTitle: { type: String, required: true },
  currentEditableEvent: { type: Object, required: true },
  closeContextMenu: { type: Function, required: true },
  handleContextMenuEdit: { type: Function, required: true },
  handleContextMenuCopy: { type: Function, required: true },
  handleContextMenuDelete: { type: Function, required: true },
  handleMaskClose: { type: Function, required: true },
  handleSwitchPresentation: { type: Function, required: true },
  handleConfirmEdit: { type: Function, required: true },
  handleConfirmAndAdd: { type: Function, required: true },
  handleDeleteEditEvent: { type: Function, required: true },
  onBindEditPanelRef: { type: Function, required: false },
  onBindContextMenuPanelRef: { type: Function, required: false },
  panelStyle: { type: Object, required: false }
})

const emit = defineEmits(["update:open"])

const innerOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
})

const closePanel = () => {
  innerOpen.value = false
}

const bindEditPanelRef = (instance: unknown) => {
  props.onBindEditPanelRef?.(instance)
}

const bindContextMenuPanelRef = (instance: unknown) => {
  props.onBindContextMenuPanelRef?.(instance)
}

const handleSelect = (event: CalendarEvent, nativeEvent: MouseEvent) => {
  props.onSelectEvent({ event, nativeEvent })
}

const handleContextmenu = (event: CalendarEvent, nativeEvent: MouseEvent) => {
  props.onContextmenuEvent({ event, nativeEvent })
}
</script>
